'use client';

import { VStack, HStack, Box, css } from '@terra/ui-styled-system';
import { Select, createSelectListCollection } from '../../components/select';
import {
  BodyDefault,
  LabelDefault,
  MetaDefault,
} from '../../components/typography';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Tick01Icon, ArrowUpDownIcon } from '@terra/ui-icons';
import {
  ALLOWED_DAY_VALUES,
  DAYS_OF_WEEK,
  WORKING_HOURS_MIN,
  WORKING_HOURS_MAX,
  mergeScheduleHourPartial,
  resolveScheduleHour,
  formatScheduleTimeRangeLabel,
  normalizeHourRange,
  normalizeDaysOfWeek,
  DEFAULT_DAYS_AUTOMATIC_EXECUTIONS,
  resolveScheduleTimezoneSelection,
  buildTimezoneSelectItems,
  buildStartHourSelectItems,
  buildEndHourSelectItems,
  isValidScheduleHourRange,
  type AutomationSchedule,
} from './automationScheduleShared';
import { useBrowserTimezone } from './useBrowserTimezone';
import { AutomationToggleRow } from './AutomationToggleRow';

const DAY_TOGGLE_SIZE = '32px';
const SELECT_ITEM_GROUP_MAX_HEIGHT = '240px';

export const scheduleDayToggleItemClassName = css({
  width: DAY_TOGGLE_SIZE,
  minWidth: DAY_TOGGLE_SIZE,
  maxWidth: DAY_TOGGLE_SIZE,
  height: DAY_TOGGLE_SIZE,
  flex: `0 0 ${DAY_TOGGLE_SIZE}`,
  padding: '0',
  fontSize: 'xs',
  fontWeight: 'medium',
  borderRadius: '0',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.neutral.default',
  bg: 'bg.default',
  color: 'fg.default',
  cursor: 'pointer',
  position: 'relative',
  zIndex: 0,
  boxSizing: 'border-box',
  margin: 0,
  appearance: 'none',
  WebkitAppearance: 'none',
  '&:not(:first-of-type)': {
    marginLeft: '-1px',
  },
  '&:first-of-type': {
    borderTopLeftRadius: '4px',
    borderBottomLeftRadius: '4px',
  },
  '&:last-of-type': {
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  '&[aria-pressed=true]': {
    borderColor: 'border.accent.default',
    bg: 'bg.accent.light',
    color: 'fg.accent.default',
    zIndex: 1,
  },
  '&:focus-visible': {
    outlineWidth: '1px',
    outlineStyle: 'solid',
    outlineColor: 'border.accent.default',
    outlineOffset: '1px',
    zIndex: 2,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});
export type AutomaticExecutionsConfigurationChange = {
  smokeTestEnabled?: boolean;
  supportsParallelExecution?: boolean;
  limitExecutionTimeEnabled?: boolean;
  automationSchedule?: AutomationSchedule;
};
export type AutomaticExecutionsConfigurationProps = {
  smokeTestEnabled: boolean;
  supportsParallelExecution: boolean;
  limitExecutionTimeEnabled: boolean;
  automationSchedule: AutomationSchedule;
  onChange: (change: AutomaticExecutionsConfigurationChange) => void;
  disabled?: boolean;
};
export type AutomationDayToggleGroupProps = {
  daysOfWeek: number[];
  disabled?: boolean;
  onToggleDay: (dayValue: number) => void;
};
export function AutomationDayToggleGroup({
  daysOfWeek,
  disabled,
  onToggleDay,
}: AutomationDayToggleGroupProps) {
  const tSchedule = useTranslations('designSystem.automation.schedule');

  return (
    <HStack
      role="group"
      gap={0}
      alignSelf="flex-start"
      w="fit-content"
      maxW="full"
      flexWrap="nowrap"
    >
      {DAYS_OF_WEEK.map((day) => {
        const isSelected = daysOfWeek.includes(day.value);

        return (
          <button
            key={day.value}
            type="button"
            disabled={disabled}
            aria-label={tSchedule(`days.${day.value}`)}
            aria-pressed={isSelected}
            className={scheduleDayToggleItemClassName}
            onClick={() => onToggleDay(day.value)}
          >
            {day.short}
          </button>
        );
      })}
    </HStack>
  );
}

export function AutomaticExecutionsConfiguration({
  smokeTestEnabled,
  supportsParallelExecution,
  limitExecutionTimeEnabled,
  automationSchedule,
  onChange,
  disabled = false,
}: AutomaticExecutionsConfigurationProps) {
  const tSchedule = useTranslations('designSystem.automation.schedule');
  const t = useTranslations('designSystem.automation.executions');
  const browserTimezone = useBrowserTimezone();
  const daysOfWeek = useMemo(
    () =>
      normalizeDaysOfWeek(
        automationSchedule?.daysOfWeek,
        DEFAULT_DAYS_AUTOMATIC_EXECUTIONS,
      ),
    [automationSchedule?.daysOfWeek],
  );
  const [startHour, endHour] = useMemo(
    () =>
      normalizeHourRange({
        startHour: resolveScheduleHour(
          automationSchedule?.startHour,
          WORKING_HOURS_MIN,
        ),
        endHour: resolveScheduleHour(
          automationSchedule?.endHour,
          WORKING_HOURS_MAX,
        ),
      }),
    [automationSchedule?.startHour, automationSchedule?.endHour],
  );
  const selectedTimezone = useMemo(
    () =>
      resolveScheduleTimezoneSelection({
        scheduleTimezone: automationSchedule?.timezone,
        browserTimezone,
      }),
    [browserTimezone, automationSchedule?.timezone],
  );
  const timezoneItems = useMemo(
    () =>
      buildTimezoneSelectItems({
        browserTimezone,
        selectedTimezone,
      }),
    [browserTimezone, selectedTimezone],
  );
  const timezoneCollection = useMemo(
    () => createSelectListCollection({ items: timezoneItems }),
    [timezoneItems],
  );
  const startHourItems = useMemo(
    () => buildStartHourSelectItems(endHour),
    [endHour],
  );
  const endHourItems = useMemo(
    () => buildEndHourSelectItems(startHour),
    [startHour],
  );
  const startHourCollection = useMemo(
    () => createSelectListCollection({ items: startHourItems }),
    [startHourItems],
  );
  const endHourCollection = useMemo(
    () => createSelectListCollection({ items: endHourItems }),
    [endHourItems],
  );
  const emitSchedule = (partial: Partial<AutomationSchedule>) => {
    const hours = mergeScheduleHourPartial({
      partial,
      current: { startHour, endHour },
    });

    onChange({
      automationSchedule: {
        daysOfWeek: partial.daysOfWeek ?? daysOfWeek,
        startHour: hours.startHour,
        endHour: hours.endHour,
        timezone:
          partial.timezone !== undefined ? partial.timezone : selectedTimezone,
      },
    });
  };

  const handleDayToggle = (dayValue: number) => {
    if (disabled) return;
    if (!ALLOWED_DAY_VALUES.includes(dayValue)) return;
    const isSelected = daysOfWeek.includes(dayValue);

    if (isSelected && daysOfWeek.length <= 1) return;
    const newDays = isSelected
      ? daysOfWeek.filter((d) => d !== dayValue).sort((a, b) => a - b)
      : [...daysOfWeek, dayValue].sort((a, b) => a - b);

    emitSchedule({ daysOfWeek: newDays });
  };

  const handleTimezoneChange = (value: string[]) => {
    if (disabled || value.length === 0) return;
    emitSchedule({ timezone: value[0] });
  };

  const handleStartHourChange = (value: string[]) => {
    if (disabled || value.length === 0) return;
    const nextStart = Number.parseInt(value[0], 10);

    if (
      Number.isNaN(nextStart) ||
      !isValidScheduleHourRange({ startHour: nextStart, endHour })
    ) {
      return;
    }

    emitSchedule({ startHour: nextStart });
  };

  const handleEndHourChange = (value: string[]) => {
    if (disabled || value.length === 0) return;
    const nextEnd = Number.parseInt(value[0], 10);

    if (
      Number.isNaN(nextEnd) ||
      !isValidScheduleHourRange({ startHour, endHour: nextEnd })
    ) {
      return;
    }

    emitSchedule({ endHour: nextEnd });
  };

  return (
    <VStack gap={3} alignItems="stretch" w="full">
      <AutomationToggleRow
        label={t('runAutomated.label')}
        tooltipTitle={t('runAutomated.tooltip')}
        checked={smokeTestEnabled}
        onCheckedChange={(checked) => onChange({ smokeTestEnabled: checked })}
        yesLabel={t('yes')}
        noLabel={t('no')}
        disabled={disabled}
      />

      {smokeTestEnabled && (
        <>
          <AutomationToggleRow
            label={t('parallel.label')}
            tooltipTitle={t('parallel.tooltip')}
            checked={supportsParallelExecution}
            onCheckedChange={(checked) =>
              onChange({ supportsParallelExecution: checked })
            }
            yesLabel={t('yes')}
            noLabel={t('no')}
            disabled={disabled}
          />

          <AutomationToggleRow
            label={t('limitTime.label')}
            tooltipTitle={t('limitTime.tooltip')}
            checked={limitExecutionTimeEnabled}
            onCheckedChange={(checked) =>
              onChange({ limitExecutionTimeEnabled: checked })
            }
            yesLabel={t('yes')}
            noLabel={t('no')}
            disabled={disabled}
          />

          {limitExecutionTimeEnabled && (
            <VStack gap={3} alignItems="stretch" w="full">
              <BodyDefault color="fg.default">
                {t('timeLimitation.title')}
              </BodyDefault>

              <VStack gap={2} alignItems="stretch">
                <LabelDefault color="fg.neutral.muted">
                  {tSchedule('daysOfWeek')}
                </LabelDefault>
                <AutomationDayToggleGroup
                  daysOfWeek={daysOfWeek}
                  disabled={disabled}
                  onToggleDay={handleDayToggle}
                />
              </VStack>

              <VStack gap={2} alignItems="stretch" w="full">
                <LabelDefault color="fg.neutral.muted">
                  {tSchedule('timezone')}
                </LabelDefault>
                <Box w="full">
                  <Select.Root
                    positioning={{ sameWidth: true }}
                    collection={timezoneCollection}
                    value={[selectedTimezone]}
                    onValueChange={(e) => handleTimezoneChange(e.value)}
                    disabled={disabled}
                    size="sm"
                    width="full"
                  >
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText
                          placeholder={tSchedule('selectTimezone')}
                        />
                        <ArrowUpDownIcon />
                      </Select.Trigger>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        <Select.ItemGroup
                          maxH={SELECT_ITEM_GROUP_MAX_HEIGHT}
                          overflowY="auto"
                        >
                          {timezoneItems.map((timezoneOption) => (
                            <Select.Item
                              key={timezoneOption.value}
                              item={timezoneOption}
                            >
                              <Select.ItemText>
                                {timezoneOption.label}
                              </Select.ItemText>
                              <Select.ItemIndicator>
                                <Tick01Icon size={16} />
                              </Select.ItemIndicator>
                            </Select.Item>
                          ))}
                        </Select.ItemGroup>
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </Box>
              </VStack>

              <VStack gap={2} alignItems="stretch" w="full">
                <LabelDefault color="fg.neutral.muted">
                  {tSchedule('timeRange')}
                </LabelDefault>
                <HStack gap={2} alignItems="center" w="full">
                  <Box flex={1} minW={0}>
                    <Select.Root
                      positioning={{ sameWidth: true }}
                      collection={startHourCollection}
                      value={[String(startHour)]}
                      onValueChange={(e) => handleStartHourChange(e.value)}
                      disabled={disabled}
                      size="sm"
                      width="full"
                    >
                      <Select.Control>
                        <Select.Trigger>
                          <Select.ValueText />
                          <ArrowUpDownIcon />
                        </Select.Trigger>
                      </Select.Control>
                      <Select.Positioner>
                        <Select.Content>
                          <Select.ItemGroup
                            maxH={SELECT_ITEM_GROUP_MAX_HEIGHT}
                            overflowY="auto"
                          >
                            {startHourItems.map((item) => (
                              <Select.Item key={item.value} item={item}>
                                <Select.ItemText>{item.label}</Select.ItemText>
                                <Select.ItemIndicator>
                                  <Tick01Icon size={16} />
                                </Select.ItemIndicator>
                              </Select.Item>
                            ))}
                          </Select.ItemGroup>
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  </Box>
                  <MetaDefault color="fg.neutral.subtle" flexShrink={0}>
                    {tSchedule('timeRangeTo')}
                  </MetaDefault>
                  <Box flex={1} minW={0}>
                    <Select.Root
                      positioning={{ sameWidth: true }}
                      collection={endHourCollection}
                      value={[String(endHour)]}
                      onValueChange={(e) => handleEndHourChange(e.value)}
                      disabled={disabled}
                      size="sm"
                      width="full"
                    >
                      <Select.Control>
                        <Select.Trigger>
                          <Select.ValueText />
                          <ArrowUpDownIcon />
                        </Select.Trigger>
                      </Select.Control>
                      <Select.Positioner>
                        <Select.Content>
                          <Select.ItemGroup
                            maxH={SELECT_ITEM_GROUP_MAX_HEIGHT}
                            overflowY="auto"
                          >
                            {endHourItems.map((item) => (
                              <Select.Item key={item.value} item={item}>
                                <Select.ItemText>{item.label}</Select.ItemText>
                                <Select.ItemIndicator>
                                  <Tick01Icon size={16} />
                                </Select.ItemIndicator>
                              </Select.Item>
                            ))}
                          </Select.ItemGroup>
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  </Box>
                </HStack>
                {startHour > endHour ? (
                  <MetaDefault color="fg.neutral.muted">
                    {formatScheduleTimeRangeLabel({
                      startHour,
                      endHour,
                      nextDayLabel: tSchedule('timeRangeNextDay'),
                    })}
                  </MetaDefault>
                ) : null}
              </VStack>
            </VStack>
          )}
        </>
      )}
    </VStack>
  );
}
